// Canonical login-recipe wire format + serializer. The wizard maps its
// guided form shape onto these types and the AuthenticationTab POSTs the
// output verbatim under broker Parameters.
//
// Wire format target (must match backend pkg/lib/webauth/steps):
//   capture: { kind, name, value }
//   http:    { kind, method, url, headers?, body? }
//   extract: { kind, from, jsonpath?, regex?, save_as }

export type RecipeStepKind = 'capture' | 'http' | 'extract';

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export const HTTP_METHODS_LIST: HTTPMethod[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export interface CaptureStep {
  kind: 'capture';
  name: string;
  value: string;
}

export interface HTTPStep {
  kind: 'http';
  method: HTTPMethod;
  url: string;
  headers: KV[];
  body: string;
}

export interface ExtractStep {
  kind: 'extract';
  // 'response_body' | 'response_header:<name>' | 'response_cookie:<name>'
  from: string;
  jsonpath: string;
  regex: string;
  save_as: string;
}

export type RecipeStep = CaptureStep | HTTPStep | ExtractStep;

export interface KV {
  key: string;
  value: string;
}

export interface RecipeFormData {
  label: string;
  inputs: KV[];
  steps: RecipeStep[];
}

// toRecipePayload projects the in-app recipe shape into the backend wire
// format. Empty input rows are dropped; HTTP header rows are flattened
// into a map; empty optional fields on Extract steps are omitted so the
// backend's exactly-one-of jsonpath/regex check passes.
export function toRecipePayload(form: RecipeFormData): {
  method: string;
  label: string;
  inputs: Record<string, string>;
  steps: Record<string, unknown>[];
} {
  const inputs: Record<string, string> = {};
  for (const row of form.inputs) {
    if (row.key.trim()) inputs[row.key] = row.value;
  }

  const steps = form.steps.map(s => {
    if (s.kind === 'capture') {
      return { kind: 'capture', name: s.name, value: s.value };
    }
    if (s.kind === 'http') {
      const headers: Record<string, string> = {};
      for (const row of s.headers) {
        if (row.key.trim()) headers[row.key] = row.value;
      }
      const out: Record<string, unknown> = {
        kind: 'http',
        method: s.method,
        url: s.url,
      };
      if (Object.keys(headers).length > 0) out.headers = headers;
      if (s.body) out.body = s.body;
      return out;
    }
    const out: Record<string, unknown> = {
      kind: 'extract',
      from: s.from,
      save_as: s.save_as,
    };
    if (s.jsonpath) out.jsonpath = s.jsonpath;
    if (s.regex) out.regex = s.regex;
    return out;
  });

  return { method: 'dynamic-login', label: form.label, inputs, steps };
}
